<template>
    <div class="bg-neutral-950">
        <!-- header -->
        <NewHeader></NewHeader>

        <!-- header END -->
        <!-- 1.Hero -->

        <ColorBg>
            <div id="banner-top" class="relative px-6 pt-24 pb-16 mx-auto max-w-7xl sm:py-40 lg:px-8">
                <div class="relative z-40 items-center mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8">
                    <div class="mt-14 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
                        <h1 class="font-extrabold text-5xl leading-[1.2] text-gray-200 mb-7 max-[450px]:text-3xl max-[450px]:text-center">
                            {{ t('pages.home.hero.title_prefix') }}
                            <span class="text-orange-600">{{ t('pages.home.hero.brand_name') }}</span>
                            {{ t('pages.home.hero.title_suffix') }}
                        </h1>
                        <p class="text-base font-normal text-white/50 mb-7 max-[450px]:text-center">
                            {{ t('pages.home.hero.description') }}
                        </p>
                        <div class="flex flex-col min-[450px]:flex-row items-center gap-5 mb-10 xl:mb-24">
                            <FaceSwapBtn></FaceSwapBtn>
                        </div>
                        <div class="flex flex-col min-[450px]:flex-row items-center gap-8 min-[450px]:gap-28 max-[450px]:hidden">
                            <div class="block">
                                <div class="max-[450px]:text-center font-extrabold text-2xl leading-9 text-orange-600 mb-2">
                                    {{ t('pages.home.hero.stats.creators') }}</div>
                                <p class="text-base max-[450px]:text-center font-normal text-white/50">{{ t('pages.home.hero.stats.creators_label') }}
                                </p>
                            </div>
                            <div class="block">
                                <div class="max-[450px]:text-center font-extrabold text-2xl leading-9 text-orange-600 mb-2">
                                    {{ t('pages.home.hero.stats.quality') }}</div>
                                <p class="text-base max-[450px]:text-center font-normal text-white/50">{{ t('pages.home.hero.stats.quality_label') }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <video 
                        ref="heroVideo"
                        class="h-auto mt-10 aspect-[6/5] w-full max-w-none rounded-3xl object-cover sm:mt-16 lg:max-w-none xl:row-span-2 xl:row-end-2" 
                        :loop="shouldAutoplay" 
                        :autoplay="shouldAutoplay" 
                        :muted="shouldAutoplay"
                        playsinline 
                        :preload="videoPreload"
                        loading="lazy" 
                        poster="/idx/bestfaceswap_mobile.jpg"
                        @loadstart="onVideoLoadStart"
                        @canplay="onVideoCanPlay"
                        @error="onVideoError">
                        <!-- WebM格式优先（更小的文件大小） -->
                        <!-- <source :src="videoSource.webm" type="video/webm" v-if="videoSource.webm"> -->
                        <!-- MP4格式兜底 -->
                        <source :src="videoSource.mp4" type="video/mp4">
                        <track kind="captions" src="" srclang="en" label="No captions available">
                        Your browser does not support the video tag.
                    </video>
                </div>
            </div>
        </ColorBg>
        <!-- 1.Hero END -->

        <!-- 2.Face Swap feature - SSR渲染，SEO友好 -->
        <FaceSwapFeatures></FaceSwapFeatures>
        <!-- 2.Face Swap feature END -->

        <!-- Features - SSR渲染，SEO友好 -->
        <Features></Features>
        <!-- Features END-->

        <!-- 3.Step - SSR渲染，SEO友好 -->
        <ThreeSteps></ThreeSteps>
        <!-- 3.Step END -->

        <!-- 4.testimonial - 交互组件，懒加载 -->
        <v-lazy>
            <Testimonial :items="items"></Testimonial>
        </v-lazy>
        <div class="flex justify-center mt-12 w-full">
            <FaceSwapBtn></FaceSwapBtn>
        </div>
        <!-- 4.testimonial END -->

        <!-- 5.FAQ - 交互组件，懒加载 -->
        <v-lazy>
            <Faq></Faq>
        </v-lazy>
        <!-- 5.FAQ END -->

        <!-- 6.CTA - SSR渲染，SEO友好 -->
        <Cta></Cta>
        <!-- 6.CTA END-->

        <!-- Button -->
        <!-- GlobalDownloadApp hidden - no longer guiding users to download app -->
        <!-- <GlobalDownloadApp /> -->

        <div class="fixed bottom-0 left-0 w-full lg:hidden z-40">
            <div class="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
            <div class="flex relative justify-center p-4 w-full">
                <FaceSwapBtn></FaceSwapBtn>
            </div>
        </div>
        <!-- Button END -->
        <!-- <Footer></Footer> -->
        <NewFooter class="!absolute"></NewFooter>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, defineAsyncComponent, shallowRef } from 'vue';
// import '~/assets/js/BlurGradientBg.min.js'
import NewHeader from '~/components/NewHeader.vue';
import Footer from '~/components/Footer.vue';
import NewFooter from '~/components/NewFooter.vue';
import FaceSwapBtn from '~/components/FaceSwapBtn.vue';
import ColorBg from '~/components/ColorBg.vue';
// GlobalDownloadApp hidden - no longer guiding users to download app
// import GlobalDownloadApp from '~/components/GlobalDownloadApp.vue';
// SSR友好的静态组件，直接同步导入
import Features from '~/components/Features.vue';
import ThreeSteps from '~/components/ThreeSteps.vue';
import FaceSwapFeatures from '~/components/FaceSwapFeatures.vue';
import Cta from '~/components/Cta.vue';

// 骨架屏组件
const LoadingSkeleton = {
  template: `
    <div class="animate-pulse bg-gray-800 rounded-lg p-6 mx-auto max-w-7xl">
      <div class="h-6 bg-gray-700 rounded mb-4 w-1/3 mx-auto"></div>
      <div class="h-4 bg-gray-700 rounded mb-6 w-2/3 mx-auto"></div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="h-48 bg-gray-700 rounded"></div>
        <div class="h-48 bg-gray-700 rounded"></div>
        <div class="h-48 bg-gray-700 rounded"></div>
      </div>
    </div>
  `
};

// 错误组件
const ErrorComponent = {
  template: `
    <div class="text-center p-6 text-red-400">
      <p>组件加载失败，请刷新页面重试</p>
    </div>
  `
};

// 交互组件使用异步加载，添加骨架屏和错误处理
const Testimonial = defineAsyncComponent({
  loader: () => import('~/components/Testimonial.vue'),
  loadingComponent: LoadingSkeleton,
  errorComponent: ErrorComponent,
  delay: 200,
  timeout: 10000
});

const Faq = defineAsyncComponent({
  loader: () => import('~/components/Faq.vue'),
  loadingComponent: LoadingSkeleton,
  errorComponent: ErrorComponent,
  delay: 200,
  timeout: 10000
});

const { t } = useI18n();

// Video优化相关状态
const heroVideo = ref(null);
const shouldAutoplay = ref(false);
const videoPreload = ref('none');
const videoSource = ref({
    webm: '',
    mp4: '/idx/bestfaceswap_mobile.mp4'
});

// 检测网络状态和设备性能
const checkVideoCapabilities = () => {
    // 检测网络连接
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    const isSlowConnection = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
    
    // 检测设备性能
    const isLowEndDevice = navigator.hardwareConcurrency <= 2;
    
    // 检测数据节省模式
    const isDataSaver = connection && connection.saveData;
    
    // 检测电池状态
    const battery = (navigator as any).getBattery?.();
    
    return {
        isSlowConnection,
        isLowEndDevice,
        isDataSaver,
        shouldOptimize: isSlowConnection || isLowEndDevice || isDataSaver
    };
};

// 动态设置video配置
const setupVideoOptimization = () => {
    const capabilities = checkVideoCapabilities();
    
    if (capabilities.shouldOptimize) {
        // 低性能设备或慢网络：不自动播放，最小预加载
        shouldAutoplay.value = false;
        videoPreload.value = 'none';
    } else {
        // 高性能设备且网络良好：自动播放，预加载元数据
        shouldAutoplay.value = true;
        videoPreload.value = 'metadata';
        
        // 检查WebM支持
        const video = document.createElement('video');
        if (video.canPlayType('video/webm').replace(/no/, '')) {
            videoSource.value.webm = '/idx/bestfaceswap_mobile.webm';
        }
    }
};

// Video事件处理
const onVideoLoadStart = () => {
    console.log('Video loading started');
};

const onVideoCanPlay = () => {
    console.log('Video can play');
};

const onVideoError = (event: Event) => {
    console.warn('Video load error:', event);
    // 自动降级到poster图片
    if (heroVideo.value) {
        heroVideo.value.style.display = 'none';
    }
};

useHead({
    title: t('pages.home.hero.title'),
    meta: [
        {
            name: 'description',
            content: t('pages.home.hero.description')
        }
    ],
    // canonical 由 plugins/i18n-seo.ts 全局注入（基于 NEXT_PUBLIC_SITE_URL，自动保证与 hreflang 一致）
});

// 优化 i18n 计算，避免重复计算
const items = shallowRef([]);

onMounted(() => {
  // 设置video优化配置
  setupVideoOptimization();
  
  // 一次性构建 testimonials 数组，避免响应式重计算
  const testimonials = [];
  for (let i = 0; i < 9; i++) {
    const avatar = `/idx/avater_${String(i + 1).padStart(2, '0')}.png`;
    const name = t(`pages.home.testimonials.items.${i}.name`);
    const content = t(`pages.home.testimonials.items.${i}.content`);
    
    // Only add if name translation exists (not fallback key)
    if (name !== `pages.home.testimonials.items.${i}.name`) {
      testimonials.push({ avatar, name, content });
    }
  }
  items.value = testimonials;
});
</script>
